<template>
  <section class="credence-advantage">
    <div class="container">
      <!-- Government Support Badge -->
      <div class="support-badge">
        <span>Supported by Cyberport, Hong Kong SAR Government initiative</span>
      </div>

      <!-- Main Heading Section -->
      <h2 class="main-heading">The Credence Advantage</h2>
      <p class="tagline">Trade-Driven Insights. Tech-Driven Speed.</p>
      <p class="description">
        In the digital world, we harness data not as a fixed asset, but as an
        intelligent, ever-adapting resource. Our platform leverages intelligent
        extraction, pattern recognition, and validation layers across billions
        of data points—helping banks and companies detect risks, uncover
        opportunities, and take action across borders.
      </p>

      <!-- Process Flow for small screens-->
      <div class="process-flow" v-if="screenWidth < 1024">
        <div
          v-for="(item, index) in processFlowCards"
          :key="index"
          class="process-item"
          :class="'step-' + (index + 1)"
        >
          <div class="icon-container">
            <img :src="item.image" :alt="item.name" class="process-icon" />
          </div>
          <p>{{ item.name }}</p>
        </div>
      </div>
      <!-- Process Flow -->
      <div class="process-flow" v-else>
        <template
          v-for="(item, index) in processFlowCards"
          :key="index + item?.name"
        >
          <div class="process-item">
            <div class="icon-container">
              <img :src="item?.image" :alt="item?.name" class="process-icon" />
            </div>
            <p>{{ item?.name }}</p>
          </div>

          <!-- Add connector only if not the last item -->
          <div
            class="connector"
            v-if="index < processFlowCards.length - 1"
          ></div>
        </template>
      </div>
      <!-- Feature Cards -->
      <div class="advantage-list-cards">
        <div class="advantage-row-1">
          <div class="advantage-1">
            <div class="content-adv-1">
              <div class="made-for-asia">Made for Asia</div>
              <div class="coverage-of-important">
                100% coverage of important Asian Trade corridors such as China,
                India, Hong Kong, Singapore, Taiwan for SMEs and large
                companies.
              </div>
            </div>
            <div class="img-1-wrapper">
              <div class="illustration-adv-1" :style="{ backgroundImage: `url(${getCDNlink('assets/new-assets/illustration-adv-1.webp')})` }">
                <img
                  class="globe-gif-icon"
                  alt=""
                  :src="getCDNlink('assets/new-assets/Globe-GIF.gif')"
                />
              </div>
            </div>
          </div>
          <div class="advantage-2">
            <div class="illustration-adv-2" :style="{ backgroundImage: `url(${getCDNlink('assets/new-assets/illustration-adv-2.webp')})` }">
              <img
                class="cargo-ship-1-icon"
                alt=""
                :src="getCDNlink('assets/new-assets/cargo-ship.gif')"
              />
            </div>
            <div class="content-adv-2">
              <div class="made-for-asia">Trade Risk Intelligence</div>
              <div class="coverage-of-important">
                Comprehensive reports include shipment history, products traded,
                and fraud pattern detection.
              </div>
            </div>
          </div>
        </div>
        <div class="advantage-row-1">
          <div class="advantage-2">
            <div class="illustration-adv-3" :style="{ backgroundImage: `url(${getCDNlink('assets/new-assets/illustration-adv-3.webp')})` }">
              <img
                class="bolt-fast-icon"
                alt=""
                :src="getCDNlink('assets/new-assets/Bolt-Fast.gif')"
              />
            </div>
            <div class="content-adv-2">
              <div class="made-for-asia">Fast, relevant, and reliable</div>
              <div class="coverage-of-important">
                We leverage cutting-edge technology to deliver fresh, official
                data from key markets in hours instead of days.
              </div>
            </div>
          </div>
          <div class="advantage-1">
            <div class="content-card-4">
              <div class="made-for-asia">
                Fresh data powers critical decisions
              </div>
              <div class="coverage-of-important">
                We provide updated business intelligence on international trade
                counterparts to help our customers make informed decisions.
              </div>
            </div>
            <div class="illustration-card-4">
              <img
                class="data-network-illustration"
                alt=""
                :src="
                  getCDNlink('assets/new-assets/Data-Network-Illustration.webp')
                "
              />
              <img
                class="informed-icon"
                alt=""
                :src="getCDNlink('assets/new-assets/Informed.gif')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import config from '@/config';

export default {
  name: "CredenceAdvantage",

  data() {
    return {
      screenWidth: window.innerWidth,
      processFlowCards: [
        {
          name: "Access Data",
          image: this.getCDNlink('assets/new-assets/access-data.gif'),
        },
        {
          name: "Extract Data through OCR/AI",
          image: this.getCDNlink('assets/new-assets/extract-data.gif'),
        },
        {
          name: "Task Automation & Data Analysis",
          image: this.getCDNlink("assets/new-assets/task-automation.gif"),
        },
        {
          name: "Validation & Checks",
          image: this.getCDNlink("assets/new-assets/validation.gif"),
        },
        {
          name: "Information Delivery",
          image: this.getCDNlink("assets/new-assets/information-delivery.gif"),
        },
      ],
    };
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    handleResize() {
      this.screenWidth = window.innerWidth;
    },
    getCDNlink(fileKey) {
      let baseUrl = "";
      if (
        process.env.NODE_ENV === "development" ||
        process.env.NODE_ENV === "staging"
      ) {
        baseUrl = config.stagingCDN;
      } else if (process.env.NODE_ENV === "production") {
        baseUrl = config.prodCDN;
      }

      return `${baseUrl}${fileKey}`;
    },
  },
};
</script>

<style scoped>
.credence-advantage {
  width: 100%;
  position: relative;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  gap: 64px;
  text-align: center;
  font-size: 16px;
  color: #1c1c1c;
  font-family: Figtree;
  height: 100%;
  padding: 80px 140px;
  text-align: justify;
}

.container {
  margin: 0;
  width: -webkit-fill-available;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Government Support Badge */
.support-badge {
  display: inline-flex;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;

  border: 1px solid #C94374;
}

.support-badge span {
  color: #C94374;
  font-size: 14px;
  font-weight: 500;
}

/* Main Heading Section */
.main-heading {
  color: #1e3a8a;
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;
}

.tagline {
  color: #1c1c1c;
  font-size: 24px;
  font-weight: 500;
  margin: 0;
  text-align: center;
}

.description {
  color: #1c1c1c;
  font-size: 18px;
  line-height: 1.3;
  text-align: center;
}

/* Process Flow */
.process-flow {
  width: 100%;
  position: relative;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(1, 114, 244, 0.2),
    rgba(255, 255, 255, 0.2)
  );
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 60px 11px;
  box-sizing: border-box;
  text-align: center;
  font-size: 14.4px;
  color: #1c1c1c;
}

.process-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 11%;
}

.icon-container {
  background-color: white;
  border: none;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border-radius: 12.8px;
}

.process-icon {
  width: 50px;
  height: auto;
}

.process-item p {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1c1c1c;
}

.connector {
  flex-grow: 1;
  height: 2px;
  background: linear-gradient(90deg, #C94374 50%, transparent 50%);
  background-size: 10px 2px;
  background-repeat: repeat-x;
}

/* Adavantage Cards */

.made-for-asia {
  align-self: stretch;
  position: relative;
  letter-spacing: -0.02em;
  line-height: 120%;
  font-weight: 600;
  font-size: 24px;
}
.coverage-of-important {
  align-self: stretch;
  position: relative;
  font-size: 18px;
  line-height: 124%;
  font-weight: 500;
}
.content-adv-1 {
  align-self: stretch;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 32px;
  gap: 8px;
}
.globe-gif-icon {
  width: 79.9px;
  height: 79.9px;
  object-fit: cover;
}
.illustration-adv-1 {
  flex: 1;
  height: 370px;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

.img-1-wrapper {
  display: contents;
}
.advantage-1 {
  align-self: stretch;
  flex: 1.3;
  border-radius: 16px;
  background: linear-gradient(180deg, #e4efff, #eef1ff);
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
}
.cargo-ship-1-icon {
  width: 80px;
  position: relative;
  border-radius: 8px;
  max-height: 100%;
  object-fit: cover;
}
.illustration-adv-2 {
  align-self: stretch;
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 27px 157px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: top;
}
.content-adv-2 {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 32px;
  gap: 8px;
}
.advantage-2 {
  align-self: stretch;
  flex: 1;
  border-radius: 16px;
  background: linear-gradient(180deg, #e4efff, #eef1ff);
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
}
.advantage-row-1 {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 32px;
}
.bolt-fast-icon {
  width: 101.4px;
  position: relative;
  max-height: 100%;
  object-fit: cover;
}
.illustration-adv-3 {
  align-self: stretch;
  height: 200px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 0px 5px;
  box-sizing: border-box;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: top;
}
.content-card-4 {
  width: 340px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 32px;
  box-sizing: border-box;
  gap: 8px;
}
.data-network-illustration {
  align-self: stretch;
  flex: 1;
  position: relative;
  max-width: 100%;
  overflow: hidden;
  max-height: 100%;
  object-fit: cover;
}
.informed-icon {
  width: 80px;
  position: relative;
  max-height: 100%;
  object-fit: cover;
}
.illustration-card-4 {
  align-self: stretch;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0px 10px;
  gap: 10px;
}
.advantage-list-cards {
  width: -webkit-fill-available;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 32px;
  text-align: left;
  font-size: 24px;
  color: #1c1c1c;
  font-family: Figtree;
}

/* Responsive Styles */
@media only screen and (min-width: 768px) and (max-width: 1023px) {
  .advantage-row-1 {
    gap: 20px;
  }
  .img-1-wrapper {
    display: flex;
    width: -webkit-fill-available;
    flex: 1;
  }
  .illustration-adv-1 {
    /* height: 200px; */
  }
  .advantage-2,
  .advantage-1 {
    flex: 1;
  }
  .credence-advantage {
    padding: 60px;
  }
  .process-flow {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(3, auto);
    justify-items: center;
    align-items: center;
    gap: 2rem;
    padding: 3rem 2rem;
    position: relative;
  }

  .support-badge {
    margin: 0;
  }

  .tagline {
    color: #1c1c1c;
    font-size: 1.25rem;
    font-weight: 500;
    margin: 1rem 0px;
    text-align: center;
  }
  .description {
    text-align: justify;
    margin: 0;
  }

  .process-item {
    position: relative;
    width: auto;
  }

  /* Grid placement */
  .step-1 {
    grid-column: 2;
    grid-row: 1;
  } /* Access Data */
  .step-2 {
    grid-column: 2;
    grid-row: 2;
  } /* Extract Data */
  .step-3 {
    grid-column: 3;
    grid-row: 2;
  } /* Task Automation */
  .step-4 {
    grid-column: 2;
    grid-row: 3;
  } /* Validation */
  .step-5 {
    grid-column: 3;
    grid-row: 3;
  } /* Information Delivery */

  /* Connectors */
  .process-item::after {
    content: "";
    position: absolute;
    background: repeating-linear-gradient(
      90deg,
      #C94374,
      #C94374 5px,
      transparent 5px,
      transparent 10px
    );
    opacity: 0.5;
  }

  /* Specific connector styles */
  .step-1::after {
    width: 2px;
    height: 50px;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
  }

  .step-2::after {
    width: 89px;
    height: 2px;
    top: 50%;
    left: 90%;
    transform: translateY(-50%);
  }

  .step-3::after {
    width: 2px;
    height: 50px;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
  }

  .step-4::after {
    width: 84px;
    height: 2px;
    top: 50%;
    left: 100%;
    transform: translateY(-50%);
  }

  .step-5::after {
    display: none;
  }
}

@media (max-width: 767px) {
  .main-heading {
    font-size: 36px;
  }

  .tagline {
    font-size: 16px;
  }
  .description {
    font-size: 14px;
  }
  .credence-advantage {
    padding: 45px 40px;
  }
  .advantage-row-1 {
    flex-direction: column;
    gap: 20px;
    width: -webkit-fill-available;
  }
  .made-for-asia {
    font-size: 18px;
  }
  .coverage-of-important {
    font-size: 14px;
    line-height: 117%;
  }
  .process-flow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    padding: 1.5rem 1rem;
  }
  .content-adv-1,
  .content-adv-2,
  .content-card-4 {
    padding: 0px 10px 15px;
    gap: 4px;
  }

  .illustration-adv-1 {
    height: 120px;
  }
  .illustration-adv-3 {
    height: 110px;
  }
  .illustration-card-4 {
    height: 160px;
  }
  .advantage-list-cards {
    gap: 20px;
  }
  .informed-icon,
  .cargo-ship-1-icon {
    width: 50px;
  }
  .bolt-fast-icon {
    width: 70px;
  }
  .globe-gif-icon {
    width: 50px;
    height: 50px;
  }
  .process-item {
    position: relative;
    width: 100%;
  }

  .process-item::after {
    content: "";
    width: 2px;
    height: 50px;
    background: repeating-linear-gradient(
      180deg,
      #C94374,
      #C94374 5px,
      transparent 5px,
      transparent 10px
    );
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0.5;
  }

  .process-item:last-child::after {
    display: none;
  }
}

@media (max-width: 480px) {
  .credence-advantage {
    padding: 25px;
  }
  .icon-container{
    width:50px;
    height:50px;
  }
  .main-heading {
    margin-top: 14px;
  }
  .advantage-row-1 {
    flex-direction: column;
    gap: 20px;
    width: -webkit-fill-available;
  }

  .content-adv-1,
  .content-adv-2,
  .content-card-4 {
    padding: 15px;
    gap: 4px;
  }
  .heading {
    margin-top: 35px;
  }
}
</style>
